(function (globalScope) {
  class WqHeaderDisclosureController {
    constructor(root, options = {}) {
      this.root = root;
      this.document = options.document || root.ownerDocument;
      this.items = Array.from(root.querySelectorAll('[data-wq-disclosure]'));
      this.closeTimers = new WeakMap();
      this.bindEvents();
    }

    buttonFor(item) { return item.querySelector('[data-wq-disclosure-button]'); }

    open(item) {
      clearTimeout(this.closeTimers.get(item));
      this.closeTimers.delete(item);
      this.closeAll({ except: item });
      item.classList.add('is-open');
      this.buttonFor(item)?.setAttribute('aria-expanded', 'true');
    }

    close(item, { restoreFocus = false } = {}) {
      if (!item) return;
      clearTimeout(this.closeTimers.get(item));
      this.closeTimers.delete(item);
      item.classList.remove('is-open');
      const button = this.buttonFor(item);
      button?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) button?.focus();
    }

    closeAll({ except = null, restoreFocus = false } = {}) {
      this.items.forEach((item) => {
        if (item !== except && item.classList.contains('is-open')) this.close(item, { restoreFocus });
      });
    }

    toggle(item) {
      if (item.classList.contains('is-open')) this.close(item);
      else this.open(item);
    }

    bindEvents() {
      this.items.forEach((item) => {
        const button = this.buttonFor(item);
        if (button) {
          item.addEventListener('pointerenter', (event) => {
            if (event.pointerType === 'touch') return;
            this.open(item);
          });
          item.addEventListener('pointerleave', (event) => {
            if (event.pointerType === 'touch') return;
            clearTimeout(this.closeTimers.get(item));
            this.closeTimers.set(item, setTimeout(() => {
              if (!item.matches(':hover') && !item.contains(this.document.activeElement)) this.close(item);
            }, 250));
          });
        }
        button?.addEventListener('click', (event) => {
          event.preventDefault();
          this.toggle(item);
        });
        button?.addEventListener('keydown', (event) => {
          if (!['Enter', ' '].includes(event.key)) return;
          event.preventDefault();
          this.toggle(item);
        });
      });

      this.document.addEventListener('click', (event) => {
        if (!this.root.contains(event.target)) this.closeAll();
      });
      this.document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const openItem = this.items.find((item) => item.classList.contains('is-open'));
        if (!openItem) return;
        event.preventDefault();
        this.close(openItem, { restoreFocus: true });
      });
    }
  }

  class WqLocalizationController {
    constructor(root) {
      this.root = root;
      if (root.hasAttribute('data-wq-localization-ready')) return;
      root.setAttribute('data-wq-localization-ready', 'true');
      root.addEventListener('toggle', (event) => this.handleToggle(event), true);
      root.addEventListener('input', (event) => this.handleSearch(event));
    }

    details() { return Array.from(this.root.querySelectorAll('[data-wq-localization]')); }

    handleToggle(event) {
      const details = event.target.closest?.('[data-wq-localization]');
      if (!details || !this.root.contains(details)) return;
      const trigger = details.querySelector(':scope > summary');
      trigger?.setAttribute('aria-expanded', String(details.open));
      if (!details.open) return;
      this.details().forEach((other) => {
        if (other !== details) other.removeAttribute('open');
      });
    }

    handleSearch(event) {
      if (!event.target.matches('[data-wq-country-search]')) return;
      const query = event.target.value.trim().toLocaleLowerCase();
      const form = event.target.closest('form');
      const select = form?.querySelector('[data-wq-country-select]');
      const empty = form?.querySelector('[data-wq-country-empty]');
      let visible = 0;
      Array.from(select?.options || []).forEach((option) => {
        const matches = query === '' || option.text.toLocaleLowerCase().includes(query);
        option.hidden = !matches;
        if (matches) visible += 1;
      });
      if (empty) empty.hidden = visible > 0;
    }
  }

  const positionMobileDrawer = (drawer, overlay, header) => {
    const top = Math.max(0, header?.getBoundingClientRect?.().top || 0);
    const value = `${top}px`;
    drawer?.style?.setProperty('--wq-mobile-nav-top', value);
    overlay?.style?.setProperty('--wq-mobile-nav-top', value);
    return top;
  };

  const observeMobileDrawerPosition = (drawer, overlay, header, announcement, Observer = globalScope.ResizeObserver) => {
    positionMobileDrawer(drawer, overlay, header);
    if (!Observer || !announcement) return null;
    const observer = new Observer(() => positionMobileDrawer(drawer, overlay, header));
    observer.observe(announcement);
    return observer;
  };

  const getWqPromotionState = (subtotal, config) => {
    const freeShipping = Number(config?.freeShipping);
    const tiers = Array.isArray(config?.tiers) ? config.tiers.map((tier) => ({
      threshold: Number(tier.threshold),
      discount: Number(tier.discount),
    })) : [];
    const thresholds = [freeShipping, ...tiers.map((tier) => tier.threshold)];
    if (tiers.length !== 3 || thresholds.some((value) => !Number.isFinite(value) || value < 0)) return null;
    if (!thresholds.every((value, index) => index === 0 || thresholds[index - 1] < value)) return null;
    if (tiers.some((tier) => !Number.isFinite(tier.discount) || tier.discount < 0)) return null;

    const total = Math.max(0, Number(subtotal) || 0);
    if (total < freeShipping) return { stage: 'shipping', remaining: freeShipping - total, discount: 0, nextDiscount: 0 };
    if (total < tiers[0].threshold) return { stage: 'tier', remaining: tiers[0].threshold - total, discount: 0, nextDiscount: tiers[0].discount };
    if (total < tiers[1].threshold) return { stage: 'tier', remaining: tiers[1].threshold - total, discount: tiers[0].discount, nextDiscount: tiers[1].discount };
    if (total < tiers[2].threshold) return { stage: 'tier', remaining: tiers[2].threshold - total, discount: tiers[1].discount, nextDiscount: tiers[2].discount };
    return { stage: 'complete', remaining: 0, discount: tiers[2].discount, nextDiscount: 0 };
  };

  const readWqPromotionConfig = (root) => ({
    freeShipping: Number(root.dataset.freeShipping),
    tiers: [1, 2, 3].map((index) => ({
      threshold: Number(root.dataset[`tier-${index}Threshold`]),
      discount: Number(root.dataset[`tier-${index}Discount`]),
    })),
  });

  if (typeof module !== 'undefined' && module.exports) module.exports = { WqHeaderDisclosureController, WqLocalizationController, positionMobileDrawer, observeMobileDrawerPosition, getWqPromotionState, readWqPromotionConfig };
  if (!globalScope.document) return;

  const document = globalScope.document;
  const focusable = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  let previousFocus = null;

  const lock = () => document.documentElement.classList.add('wq-scroll-locked');
  const unlock = () => document.documentElement.classList.remove('wq-scroll-locked');
  const trap = (event, root) => {
    if (event.key !== 'Tab') return;
    const items = [...root.querySelectorAll(focusable)].filter((node) => node.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  const closeDrawer = (drawer, overlay, trigger) => {
    drawer?.classList.remove('is-open');
    drawer?.setAttribute('aria-hidden', 'true');
    overlay?.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');
    drawer?.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
    unlock();
    (previousFocus || trigger)?.focus?.();
    document.dispatchEvent(new CustomEvent('wq:drawer:close'));
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-wq-drawer-open]');
    if (toggle) {
      const drawer = document.querySelector(toggle.dataset.wqDrawerOpen);
      const overlay = document.querySelector(toggle.dataset.wqOverlay);
      if (!drawer) return;
      previousFocus = document.activeElement;
      const header = toggle.closest('[data-wq-header]');
      if (!drawer.wqPositionObserver) {
        drawer.wqPositionObserver = observeMobileDrawerPosition(drawer, overlay, header, document.querySelector('.wq-announcement'));
      } else {
        positionMobileDrawer(drawer, overlay, header);
      }
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      overlay?.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      lock();
      drawer.querySelector(focusable)?.focus();
      document.dispatchEvent(new CustomEvent('wq:drawer:open'));
      return;
    }
    const closer = event.target.closest('[data-wq-drawer-close]');
    if (closer) {
      const drawer = document.querySelector(closer.dataset.wqDrawerClose);
      closeDrawer(drawer, document.querySelector(closer.dataset.wqOverlay), document.querySelector(`[data-wq-drawer-open="${closer.dataset.wqDrawerClose}"]`));
    }
  });

  const initAjaxProducts = (root = document) => {
    if (!window.theme?.AjaxProduct) return;
    root.querySelectorAll('.wq-product-card__form,.wq-featured__form').forEach((form) => {
      if (form.dataset.wqAjaxReady === 'true') return;
      form.dataset.wqAjaxReady = 'true';
      new window.theme.AjaxProduct(form, '.wq-button');
    });
  };

  const initCartDrawer = () => {
    if (!globalScope.theme?.CartDrawer || !document.querySelector('#CartDrawer') || globalScope.wqCartDrawer) return;
    globalScope.wqCartDrawer = new globalScope.theme.CartDrawer();
  };

  const formatPromotionMoney = (cents, root) => {
    if (root.dataset.presentmentCurrency && root.dataset.presentmentCurrency !== 'USD') return `US$${(cents / 100).toFixed(2)}`;
    if (globalScope.theme?.Currency?.formatMoney) return globalScope.theme.Currency.formatMoney(cents, root.dataset.moneyFormat || '${{amount}}');
    return `$${(cents / 100).toFixed(2)}`;
  };

  const updatePromotions = (subtotal) => {
    document.querySelectorAll('[data-wq-tiered-promotion]').forEach((root) => {
      const config = readWqPromotionConfig(root);
      const state = getWqPromotionState(subtotal, config);
      if (!state) { root.hidden = true; return; }
      root.hidden = false;
      const message = root.querySelector('[data-wq-promotion-message]');
      if (message) {
        if (state.stage === 'shipping') message.textContent = `Add ${formatPromotionMoney(state.remaining, root)} more for FREE SHIPPING`;
        else if (state.stage === 'tier' && state.discount === 0) message.textContent = `FREE SHIPPING unlocked — Add ${formatPromotionMoney(state.remaining, root)} more for ${formatPromotionMoney(state.nextDiscount, root)} OFF`;
        else if (state.stage === 'tier') message.textContent = `${formatPromotionMoney(state.discount, root)} OFF unlocked — Add ${formatPromotionMoney(state.remaining, root)} more for ${formatPromotionMoney(state.nextDiscount, root)} OFF`;
        else message.textContent = `FREE SHIPPING + ${formatPromotionMoney(state.discount, root)} OFF unlocked`;
      }
      const maximum = config.tiers[2].threshold;
      const progress = Math.min(100, Math.max(0, (Number(subtotal) || 0) / maximum * 100));
      root.style.setProperty('--wq-promo-progress', `${progress}%`);
      root.dataset.complete = String(state.stage === 'complete');
      const bar = root.querySelector('[data-wq-promotion-bar]');
      bar?.setAttribute('aria-valuenow', String(Math.min(maximum, Math.max(0, Number(subtotal) || 0))));
      bar?.setAttribute('aria-valuetext', message?.textContent || '');
    });
  };

  const refreshPromotions = () => fetch(globalScope.theme?.routes?.cart || '/cart.js', { headers: { Accept: 'application/json' } })
    .then((response) => response.ok ? response.json() : Promise.reject(new Error(`Cart ${response.status}`)))
    .then((cart) => updatePromotions(cart.items_subtotal_price))
    .catch(() => {});

  const initPromotions = () => {
    const first = document.querySelector('[data-wq-tiered-promotion]');
    if (first) updatePromotions(Number(first.dataset.cartTotal));
    if (document.documentElement.dataset.wqPromotionReady === 'true') return;
    document.documentElement.dataset.wqPromotionReady = 'true';
    document.addEventListener('cart:updated', (event) => updatePromotions(event.detail?.cart?.items_subtotal_price || 0));
    document.addEventListener('cart:build', refreshPromotions);
    document.addEventListener('ajaxProduct:added', refreshPromotions);
  };

  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-wq-featured-variant]')) return;
    const option = event.target.selectedOptions[0];
    const price = event.target.closest('.wq-featured__info')?.querySelector('[data-wq-price]');
    if (price) price.innerHTML = `<span>${option.dataset.price}</span>${option.dataset.compare ? `<s>${option.dataset.compare}</s>` : ''}`;
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      document.querySelectorAll('[data-wq-localization][open]').forEach((details) => {
        details.removeAttribute('open');
        details.querySelector('summary')?.focus();
      });
    }
    const openDrawer = document.querySelector('.wq-mobile-nav.is-open,.wq-video-modal.is-open');
    if (!openDrawer) return;
    if (event.key === 'Escape') {
      const closeButton = openDrawer.querySelector('[data-wq-drawer-close],[data-wq-video-close]');
      closeButton?.click();
    } else {
      trap(event, openDrawer);
    }
  });

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-wq-localization][open]').forEach((details) => { if (!details.contains(event.target)) details.removeAttribute('open'); });
  });

  const initHeader = (root = document) => {
    root.querySelectorAll('[data-wq-localization-set]').forEach((localization) => {
      if (!localization.wqLocalizationController) localization.wqLocalizationController = new WqLocalizationController(localization);
    });
    root.querySelectorAll('[data-wq-header]').forEach((header) => {
      if (header.wqDisclosureController) return;
      if (header.classList.contains('is-sticky')) header.closest('.shopify-section')?.classList.add('wq-header-sticky-shell');
      header.wqDisclosureController = new WqHeaderDisclosureController(header);
    });
    root.querySelectorAll('.wq-mobile-nav').forEach((drawer) => {
      drawer.querySelectorAll('details').forEach((details) => {
        if (details.dataset.wqAccordionReady === 'true') return;
        details.dataset.wqAccordionReady = 'true';
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          drawer.querySelectorAll('details[open]').forEach((sibling) => {
            if (sibling !== details) sibling.removeAttribute('open');
          });
        });
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => { initAjaxProducts(); initCartDrawer(); initHeader(); initPromotions(); });
  document.addEventListener('shopify:section:load', (event) => { initAjaxProducts(event.target); initCartDrawer(); initHeader(event.target); initPromotions(); document.dispatchEvent(new CustomEvent('wq:refresh')); });
})(typeof window !== 'undefined' ? window : globalThis);
